"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.useDelayedOpen = exports.resetUseDelayedOptionForTest = void 0;
var _lodestarUtils = require("@smartsheet/lodestar-utils");
var _react = require("react");
const NO_CLOSE_DELAY = {
  timer: -1,
  closeHandler: () => {}
};

// Helps control delay when hovering back-to-back over items w/ flyouts.
// Hovering back-to-back when flyout is showing will ignore initial flyout delay.
// These are here instead of useState because they are global for all flyout target refs.
let ignoreEnterDelay = false;
let ignoreEnterDelayTimer;
let closeDelay = NO_CLOSE_DELAY;

// We don't want to allow tests to interact with each other. Sometimes if a Flyout doesn't
// go away quickly enough (tooltips, for instance) the next test will render one before
// these flags are reset. We always want to reset these between tests. See jest.setup.ts.
let resetUseDelayedOptionForTest = () => undefined;
exports.resetUseDelayedOptionForTest = resetUseDelayedOptionForTest;
if (process.env.NODE_ENV !== "production") {
  // Don't ship this code in production.
  exports.resetUseDelayedOptionForTest = resetUseDelayedOptionForTest = () => {
    ignoreEnterDelay = false;
    clearTimeout(ignoreEnterDelayTimer);
  };
}
const endCloseDelay = () => {
  window.clearTimeout(closeDelay.timer);
  closeDelay.closeHandler();
  closeDelay = NO_CLOSE_DELAY;
};

/**
 * This hook provides an isOpen flag and HTML handlers to attach to an element to mutate it. It can
 * be used for elements that should show a popup after a short time like a contact card, tooltip,
 * etc. For things like tooltips, it supports a delayMode argument that allows the floating elements
 * to interact with each other so that opening subsequent elements is faster after the first one
 * opens. This provides a nice UX when a user is trying to look at multiple tooltips.
 */
const useDelayedOpen = function () {
  let {
    exitDelay,
    enterDelay = 500,
    resetDelay = 500,
    delayMode = "batch",
    onOpenCallback = () => {},
    onCloseCallback = () => {}
  } = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  const [isOpen, setIsOpen] = (0, _react.useState)(false);
  const enterTimer = (0, _react.useRef)();
  const handleOpen = (0, _react.useCallback)(() => {
    window.clearTimeout(ignoreEnterDelayTimer);
    if (delayMode === "batch") {
      ignoreEnterDelay = true;
    }
    setIsOpen(true);
    onOpenCallback();
  }, [delayMode, onOpenCallback]);
  const handleClose = (0, _react.useCallback)(() => {
    setIsOpen(false);
    onCloseCallback();
  }, [onCloseCallback]);
  const startCloseDelay = (0, _react.useCallback)(closeHandler => {
    endCloseDelay();
    closeDelay = {
      timer: window.setTimeout(() => {
        endCloseDelay();
      }, exitDelay),
      closeHandler
    };
  }, [exitDelay]);
  const maybeDelayedOpen = (0, _react.useCallback)(() => {
    // This ensures that the flyout doesn't open after you have quickly moved away from element.
    window.clearTimeout(enterTimer.current);
    endCloseDelay();
    if (delayMode === "each" || !ignoreEnterDelay) {
      enterTimer.current = window.setTimeout(() => {
        handleOpen();
      }, enterDelay);
    } else {
      handleOpen();
    }
  }, [delayMode, enterDelay, handleOpen]);
  const maybeDelayedClose = (0, _react.useCallback)(() => {
    /*
       Sadly cannot do the following since not all calls here have an associated event or a relatedTarget (if the relatedTarget is not focusable).
       This means there is a case that if a Tooltip Flyout is in the way of an element about to gain onMouseEnter or onFocus,
        the overlapped element's Tooltip will not be displayed since the original elements onBlur (when passing over the blocking tooltip)
        will cancel the "delayed" open for any other tooltip.
         const relatedTarget = evt?.relatedTarget as HTMLElement;
        if ( relatedTarget && relatedTarget.closest && relatedTarget.closest('[role="tooltip"]')) {
            console.log("I'm a tooltip");
            return;
        }
     */

    // this ensures that the flyout doesn't open after you have quickly moved away from element.
    window.clearTimeout(enterTimer.current);
    window.clearTimeout(ignoreEnterDelayTimer);
    if (delayMode === "batch") {
      ignoreEnterDelayTimer = window.setTimeout(() => {
        ignoreEnterDelay = false;
      }, resetDelay);
    }
    if (exitDelay) {
      startCloseDelay(handleClose);
    } else {
      handleClose();
    }
  }, [handleClose, delayMode, resetDelay, exitDelay, startCloseDelay]);

  // Clean up timers when unmounted.
  (0, _react.useEffect)(() => {
    return () => {
      endCloseDelay();
      window.clearTimeout(enterTimer.current);
      window.clearTimeout(ignoreEnterDelayTimer);
    };
  }, []);
  return {
    setIsOpen: (0, _react.useCallback)(newIsOpen => newIsOpen ? maybeDelayedOpen() : maybeDelayedClose(), [maybeDelayedOpen, maybeDelayedClose]),
    cancelEnterTimer: (0, _react.useCallback)(() => {
      ignoreEnterDelay = false;
      window.clearTimeout(enterTimer.current);
    }, []),
    isOpen,
    getDelayedOpenTargetProps: function () {
      let {
        onMouseEnter,
        onMouseLeave,
        onFocus,
        onBlur,
        ...restOverrides
      } = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
      return {
        ...restOverrides,
        onMouseEnter: (0, _lodestarUtils.callAllHandlers)(onMouseEnter, maybeDelayedOpen),
        onMouseLeave: (0, _lodestarUtils.callAllHandlers)(onMouseLeave, maybeDelayedClose),
        onFocus: (0, _lodestarUtils.callAllHandlers)(onFocus, maybeDelayedOpen),
        onBlur: (0, _lodestarUtils.callAllHandlers)(onBlur, maybeDelayedClose)
      };
    },
    cancelExitDelayTimer: endCloseDelay
  };
};
exports.useDelayedOpen = useDelayedOpen;
//# sourceMappingURL=useDelayedOpen.js.map